import {
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import GlobeIcon from "@/assets/icons/GlobeIcon";
import LockIcon from "@/assets/icons/LockIcon";
import type {
  CatalogSelectOption,
  ModelFilterType,
  ModelSourceFilter,
  Provider,
  TranslateFn,
} from "../types";
import { toTestId } from "../utils/catalog";

type CatalogToolbarProps = {
  t: TranslateFn;
  loading: boolean;
  isAdmin: boolean;
  searchTerm: string;
  provider: string;
  source: ModelSourceFilter;
  type: ModelFilterType;
  providers: Provider[];
  sources: CatalogSelectOption<ModelSourceFilter>[];
  types: CatalogSelectOption<ModelFilterType>[];
  anchorEl: HTMLElement | null;
  openMenu: boolean;
  onSearchChange: (value: string) => void;
  onProviderChange: (value: string) => void;
  onSourceChange: (value: ModelSourceFilter) => void;
  onTypeChange: (value: ModelFilterType) => void;
  onOpenMenu: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onCloseMenu: () => void;
  onOpenModal: (id: string | null, isPublic: boolean) => void;
};

const CatalogToolbar = ({
  t,
  loading,
  isAdmin,
  searchTerm,
  provider,
  source,
  type,
  providers,
  sources,
  types,
  anchorEl,
  openMenu,
  onSearchChange,
  onProviderChange,
  onSourceChange,
  onTypeChange,
  onOpenMenu,
  onCloseMenu,
  onOpenModal,
}: CatalogToolbarProps) => (
  <Grid container spacing={1} className="mb-4" alignItems="center">
    <Grid item xs={12} md={3}>
      <TextField
        data-testid="ai-catalog-search-field"
        disabled={loading}
        placeholder={t("aiCatalog.searchPlaceholder")}
        value={searchTerm}
        onChange={(event) => onSearchChange(event.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        inputProps={{ "data-testid": "ai-catalog-search-input" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    </Grid>

    <Grid item xs={12} md>
      <Box className="flex justify-end flex-wrap gap-1">
        <Select
          data-testid="ai-catalog-provider-filter"
          displayEmpty
          disabled={loading}
          value={provider}
          onChange={(event: SelectChangeEvent<string>) =>
            onProviderChange(event.target.value)
          }
        >
          <MenuItem data-testid="ai-catalog-provider-filter-all" value="">
            {t("aiCatalog.allProviders")}
          </MenuItem>
          {providers.map((item) => (
            <MenuItem
              data-testid={`ai-catalog-provider-filter-option-${toTestId(item.name)}`}
              key={item.id || item.name}
              value={item.name}
            >
              {item.name}
            </MenuItem>
          ))}
        </Select>

        <Select
          data-testid="ai-catalog-source-filter"
          displayEmpty
          disabled={loading}
          value={source}
          onChange={(event: SelectChangeEvent<ModelSourceFilter>) =>
            onSourceChange(event.target.value as ModelSourceFilter)
          }
        >
          <MenuItem data-testid="ai-catalog-source-filter-all" value="">
            {t("aiCatalog.allSources")}
          </MenuItem>
          {sources.map((item) => (
            <MenuItem
              data-testid={`ai-catalog-source-filter-option-${item.value}`}
              key={item.value}
              value={item.value}
            >
              {item.label}
            </MenuItem>
          ))}
        </Select>

        <Select
          data-testid="ai-catalog-type-filter"
          displayEmpty
          disabled={loading}
          value={type}
          onChange={(event: SelectChangeEvent<ModelFilterType>) =>
            onTypeChange(event.target.value as ModelFilterType)
          }
        >
          <MenuItem data-testid="ai-catalog-type-filter-all" value="">
            {t("aiCatalog.allTypes")}
          </MenuItem>
          {types.map((item) => (
            <MenuItem
              data-testid={`ai-catalog-type-filter-option-${item.value}`}
              key={item.value}
              value={item.value}
            >
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Grid>

    <Grid item>
      <Button
        data-testid="ai-catalog-register-model-menu-button"
        disabled={loading}
        variant="contained"
        color="primary"
        startIcon={<AddIcon className="text-[var(--primary-foreground)]" />}
        endIcon={
          <KeyboardArrowDownIcon
            className={`text-[var(--primary-foreground)] transition-transform duration-200 ${
              openMenu ? "rotate-180" : "rotate-0"
            }`}
          />
        }
        onClick={onOpenMenu}
      >
        {t("aiCatalog.registerModel")}
      </Button>
      <Menu anchorEl={anchorEl} open={openMenu} onClose={onCloseMenu}>
        {isAdmin ? (
          <MenuItem
            data-testid="ai-catalog-register-public-model-menu-item"
            onClick={() => onOpenModal(null, true)}
          >
            <GlobeIcon className="h-3 w-3 text-[var(--foreground)] me-1" />
            <Typography className="text-sm font-normal text-[var(--foreground)]">
              {t("aiCatalog.newPublicModel")}
            </Typography>
          </MenuItem>
        ) : null}
        <MenuItem
          data-testid="ai-catalog-register-private-model-menu-item"
          onClick={() => onOpenModal(null, false)}
        >
          <LockIcon className="h-3 w-3 text-[var(--foreground)] me-1" />
          <Typography className="text-sm font-normal text-[var(--foreground)]">
            {t("aiCatalog.newPrivateModel")}
          </Typography>
        </MenuItem>
      </Menu>
    </Grid>
  </Grid>
);

export default CatalogToolbar;
